import { IconButton, IconClose } from "@lcc/ui";
import { useEffect, useId, useRef } from "react";

/**
 * Accessible modal in the Hub look (white panel over the scrim): role="dialog", labelled by its title, focus moves in on open and back on close,
 * Escape closes, Tab is kept inside.
 */
export function Dialog({
  title,
  onClose,
  children,
  focusContainer = false,
}: {
  title: string;
  /** Focus the dialog itself rather than its first control (a locked form: nothing to type into). */
  focusContainer?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const first = focusContainer
      ? null
      : ref.current?.querySelector<HTMLElement>(
          "input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([data-dialog-close]):not([disabled])",
        );
    (first ?? ref.current)?.focus();
    return () => previous?.focus?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus once, when the dialog opens
  }, []);

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab" || !ref.current) return;
    const focusable = Array.from(
      ref.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="lcc-root lcc-dialog fer-dialog-layer">
      <div className="lcc-dialog__scrim" aria-hidden="true" />
      <div
        ref={ref}
        className="lcc-dialog__box fer-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="lcc-detail__head">
          <h2 id={titleId} className="lcc-dialog__title lcc-detail__titles">
            {title}
          </h2>
          <IconButton label="Close this dialog" size="sm" icon={<IconClose size={16} />} onClick={onClose} data-dialog-close />
        </div>
        {children}
      </div>
    </div>
  );
}
